// ============================================================================
// CANCUN100 — FichaLectura.js  (Bloque 29: "Mi ficha" en solo lectura y en gris, dentro de Mi panel)
// ----------------------------------------------------------------------------
// Una vez enviada, el agente ve su ficha completa pero NO puede editarla: campos deshabilitados, CLABE
// enmascarada (**** 1234), archivos como miniatura sin descarga. Leyenda: pídeselo a administración.
// Encabezado: "Mi ficha · v{n} · enviada el <fecha>".
// ============================================================================
import React, { useState, useEffect } from 'react';
import { RADIO, RADIO_TARJETA, campo as estiloCampo, etiqueta as estiloEtiqueta } from './tema';
import { ETIQUETA_CAMPO, TIPOS_ID, cargarFicha, urlFirmada, enmascararClabe, fmtFechaCorta } from './ficha';

const GRUPOS = [
  { titulo: 'Datos personales', campos: ['nombre_completo', 'fecha_nacimiento', 'celular', 'correo_personal', 'direccion', 'talla_playera'] },
  { titulo: 'Contacto de emergencia', campos: ['emergencia_nombre', 'emergencia_telefono', 'emergencia_parentesco'] },
  { titulo: 'Identificación', campos: ['identificacion_tipo', 'identificacion_url', 'comprobante_domicilio_url'] },
  { titulo: 'Datos bancarios', campos: ['banco', 'clabe', 'titular_cuenta', 'caratula_bancaria_url'] },
  { titulo: 'Notas', campos: ['notas'] },
];
const ARCHIVOS = ['identificacion_url', 'comprobante_domicilio_url', 'caratula_bancaria_url'];

export default function FichaLectura({ C, agente }) {
  const [ficha, setFicha] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [vistas, setVistas] = useState({});
  const [err, setErr] = useState('');
  const agenteId = agente && agente.id;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const fi = await cargarFicha(agenteId);
        if (!vivo) return;
        setFicha(fi || {});
        const vs = {};
        await Promise.all(ARCHIVOS.map(async (c) => { if (fi && fi[c]) { try { vs[c] = await urlFirmada(fi[c], 600); } catch { /* sin vista */ } } }));
        if (vivo) setVistas(vs);
      } catch (e) { if (vivo) setErr(e.message || 'No se pudo cargar tu ficha.'); }
      finally { if (vivo) setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [agenteId, agente && agente.ficha_completa_en]); // eslint-disable-line

  const campo = { ...estiloCampo(C), opacity: 0.6, cursor: 'not-allowed', color: C.muted };
  const lbl = estiloEtiqueta(C);
  const version = (ficha && ficha.version) || 1;
  const valorDe = (c) => {
    const v = ficha ? ficha[c] : null;
    if (v == null || v === '') return '';
    if (c === 'clabe') return enmascararClabe(v);
    if (c === 'fecha_nacimiento') return fmtFechaCorta(v);
    if (c === 'identificacion_tipo') return (TIPOS_ID.find(([k]) => k === v) || [null, v])[1];
    return String(v);
  };
  const miniatura = (c) => {
    const u = vistas[c];
    if (!u) return <div style={{ ...campo, display: 'flex', alignItems: 'center' }}>—</div>;
    const esPdf = /\.pdf(\?|$)/i.test(String(ficha[c] || ''));
    return esPdf
      ? <div style={{ ...campo, display: 'flex', alignItems: 'center', gap: 8 }}>📄 PDF guardado</div>
      : <img src={u} alt={ETIQUETA_CAMPO[c]} draggable={false} onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: RADIO, border: `1px solid ${C.border}`, filter: 'grayscale(.6) opacity(.8)', pointerEvents: 'none', userSelect: 'none', display: 'block' }} />;
  };

  return (
    <div style={{ background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, boxShadow: 'var(--au-shadow-card)', padding: 18, marginBottom: 14 }} aria-label="Mi ficha (solo lectura)">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Mi ficha <span style={{ color: C.muted, fontWeight: 500 }}>· v{version}{agente.ficha_completa_en ? ` · enviada el ${fmtFechaCorta(agente.ficha_completa_en)}` : ''}</span></div>
        <span style={{ fontSize: 12, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 'var(--au-radius-sm)', padding: '2px 8px' }}>🔒 Solo lectura</span>
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Si necesitas corregir algo, pídeselo a administración.</div>
      {cargando && <div style={{ color: C.muted, fontSize: 14 }}>Cargando…</div>}
      {err && <div className="au-banner au-banner--error" style={{ fontSize: 12 }}>{err}</div>}
      {!cargando && !err && GRUPOS.map((g) => (
        <fieldset key={g.titulo} disabled style={{ border: 'none', padding: 0, margin: '0 0 10px', minWidth: 0 }}>
          <legend style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, padding: 0 }}>{g.titulo}</legend>
          {g.campos.map((c) => (
            <label key={c} style={{ display: 'block', marginBottom: 8 }}>
              <span style={lbl}>{ETIQUETA_CAMPO[c]}</span>
              {ARCHIVOS.includes(c) ? miniatura(c) : <input type="text" value={valorDe(c)} disabled readOnly aria-readonly="true" style={campo} />}
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  );
}
